import type { HTMLAttributes } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { faFileArrowDown, faFolder, faPaperclip, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../Accordion';

export interface FileInputProps {
    name: string;
    dataTestid?: string;
    disabled?: boolean;
    multiple?: boolean;
    value?: Array<File | { name: string; id?: string; url?: string }>;
    setValue: (name: string, value: Array<File | { name: string; id?: string; url?: string }>) => void;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    defaultValue?: Array<File | { name: string; id?: string; url?: string }>;
    required?: boolean;
    errorMessage?: string;
    withAccordion?: boolean;
    showIcon?: boolean;
}

export const FileInput = ({
    name,
    value,
    dataTestid,
    htmlAttributes,
    setValue,
    disabled = false,
    multiple = true,
    required = false,
    errorMessage,
    withAccordion = false,
    showIcon = false,
}: FileInputProps) => {
    const inputRef = useRef(null);
    const { t } = useTranslation();

    const updateInputFiles = (files: Array<File | { name: string; id?: string; url?: string }>) => {
        if (!inputRef?.current) return;

        const nativeFiles = files.filter((f) => f instanceof File) as File[];

        if (nativeFiles.length > 0) {
            const dt = new DataTransfer();
            nativeFiles.forEach((f) => dt.items.add(f));
            (inputRef.current as HTMLInputElement).files = dt.files;
        }

        setValue(name, files);
    };

    const uuid = uuidv4();

    return (
        <div
            data-testid={dataTestid}
            data-component-name='FileInput'
            className='flex w-full min-w-0 gap-3'
            {...htmlAttributes}
        >
            {showIcon && (
                <div className='size-6'>
                    <FontAwesomeIcon
                        icon={faFolder}
                        className='py-2.5 w-6 shrink-0 text-gray-500'
                    />
                </div>
            )}
            <div className='shrink min-w-0'>
                <input
                    type='file'
                    id={`${name}-file-input-${uuid}`}
                    className='hidden'
                    name={name}
                    ref={inputRef}
                    multiple={multiple}
                    disabled={disabled}
                    onChange={async (e) => {
                        const newFiles = [...(e?.target?.files ?? [])];
                        const newFileNames = new Set(newFiles.map((f) => f.name));
                        const allFiles = value
                            ? [...value.filter((file) => !newFileNames.has(file.name)), ...newFiles]
                            : newFiles;
                        updateInputFiles(allFiles);
                        // Réinitialise la valeur native pour que Chrome redéclenche onChange
                        // même si le même fichier est sélectionné à nouveau
                        e.target.value = '';
                    }}
                />
                <label
                    htmlFor={`${name}-file-input-${uuid}`}
                    className={clsx(
                        [
                            'flex w-fit items-center gap-2 rounded-md border border-solid border-gray-300 px-5 py-2 text-sm hover:bg-gray-100 active:bg-gray-200 active:outline-hidden',
                        ],
                        {
                            'cursor-not-allowed': disabled,
                            'bg-gray-100': disabled,
                            'bg-white': !disabled,
                            'text-gray-800': !disabled,
                            'text-gray-400': disabled,
                            'border-red-500': errorMessage,
                            'text-red-500': errorMessage,
                        }
                    )}
                    role='button'
                    tabIndex={0}
                >
                    <FontAwesomeIcon icon={faFileArrowDown} />
                    {t('Add a document')} {required ? <span className='text-red-500'>*</span> : null}
                </label>

                {errorMessage || (value || []).length > 0 ? (
                    <div className='mt-2'>
                        {errorMessage && <p className='text-xs text-red-500'>{errorMessage}</p>}
                        {withAccordion && (value || []).length > 0 ? (
                            <Accordion
                                type='single'
                                collapsible
                            >
                                <AccordionItem value='documents'>
                                    <AccordionTrigger className='m-0 p-0 hover:no-underline cursor-pointer font-normal text-sm'>
                                        {t('Document ({{count}})', { count: (value || []).length })}
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <ul className='grow'>
                                            {(value || []).map((f, index) => (
                                                <li
                                                    key={f.name}
                                                    className='flex items-center text-xs'
                                                    title={`${f.name}-${index}`}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPaperclip}
                                                        className='mr-2 text-gray-500'
                                                    />
                                                    <span className='overflow-x-hidden text-ellipsis whitespace-nowrap leading-relaxed'>
                                                        {f.name}
                                                    </span>
                                                    <FontAwesomeIcon
                                                        icon={faTrashCan}
                                                        className='ml-auto cursor-pointer p-2 text-red-400'
                                                        onClick={() => {
                                                            const filesWithoutDeleted = (value || []).filter(
                                                                (e) => e.name !== f.name
                                                            );
                                                            updateInputFiles(filesWithoutDeleted);
                                                        }}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        ) : (
                            <ul className='min-w-0 shrink'>
                                {(value || []).map((f, index) => (
                                    <li
                                        key={f.name}
                                        className='flex items-center text-sm'
                                        title={`${f.name}-${index}`}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPaperclip}
                                            className='mr-2 text-gray-500'
                                        />
                                        <span className='truncate'>{f.name}</span>
                                        <FontAwesomeIcon
                                            icon={faTrashCan}
                                            className='ml-auto cursor-pointer p-2 text-red-400'
                                            onClick={() => {
                                                const filesWithoutDeleted = (value || []).filter(
                                                    (e) => e.name !== f.name
                                                );
                                                updateInputFiles(filesWithoutDeleted);
                                            }}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
